// Finance calculation utilities for calculator pages

/**
 * Calculate monthly repayment for an asset finance loan with optional balloon
 * Uses standard amortization formula adjusted for balloon/residual
 */
export function calculateRepayment({ assetPrice, deposit, termYears, interestRate, balloonPercent }) {
  const loanAmount = assetPrice - deposit;
  const balloonAmount = assetPrice * (balloonPercent / 100);
  const monthlyRate = interestRate / 100 / 12;
  const numPayments = termYears * 12;

  if (monthlyRate === 0) {
    const monthly = (loanAmount - balloonAmount) / numPayments;
    return {
      monthlyRepayment: Math.max(0, monthly),
      totalInterest: 0,
      totalRepayment: loanAmount,
      balloonAmount,
      loanAmount
    };
  }

  // PV of balloon payment
  const pvBalloon = balloonAmount / Math.pow(1 + monthlyRate, numPayments);
  const effectiveLoan = loanAmount - pvBalloon;

  const monthlyRepayment = effectiveLoan *
    (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
    (Math.pow(1 + monthlyRate, numPayments) - 1);

  const totalRepayment = (monthlyRepayment * numPayments) + balloonAmount;
  const totalInterest = totalRepayment - loanAmount;

  return {
    monthlyRepayment: Math.max(0, monthlyRepayment),
    totalInterest: Math.max(0, totalInterest),
    totalRepayment: Math.max(0, totalRepayment),
    balloonAmount,
    loanAmount
  };
}

/**
 * Calculate lease vs buy comparison
 */
export function calculateLeaseVsBuy({ assetPrice, deposit, termYears, buyRate, leaseRate, residualPercent }) {
  const buyResult = calculateRepayment({
    assetPrice, deposit, termYears,
    interestRate: buyRate, balloonPercent: 0
  });

  const leaseResult = calculateRepayment({
    assetPrice, deposit: 0, termYears,
    interestRate: leaseRate, balloonPercent: residualPercent
  });

  return {
    buy: {
      monthlyPayment: buyResult.monthlyRepayment,
      totalCost: buyResult.totalRepayment,
      totalInterest: buyResult.totalInterest,
      ownsAsset: true
    },
    lease: {
      monthlyPayment: leaseResult.monthlyRepayment,
      totalCost: leaseResult.totalRepayment,
      totalInterest: leaseResult.totalInterest,
      residualValue: assetPrice * (residualPercent / 100),
      ownsAsset: false
    },
    monthlySaving: buyResult.monthlyRepayment - leaseResult.monthlyRepayment,
    totalDifference: buyResult.totalRepayment - leaseResult.totalRepayment
  };
}

/**
 * Calculate refinance savings
 */
export function calculateRefinanceSavings({ currentBalance, currentRate, newRate, remainingYears }) {
  const currentMonthly = calculateRepayment({
    assetPrice: currentBalance, deposit: 0,
    termYears: remainingYears, interestRate: currentRate, balloonPercent: 0
  });

  const newMonthly = calculateRepayment({
    assetPrice: currentBalance, deposit: 0,
    termYears: remainingYears, interestRate: newRate, balloonPercent: 0
  });

  return {
    currentMonthly: currentMonthly.monthlyRepayment,
    newMonthly: newMonthly.monthlyRepayment,
    monthlySaving: currentMonthly.monthlyRepayment - newMonthly.monthlyRepayment,
    totalCurrentCost: currentMonthly.totalRepayment,
    totalNewCost: newMonthly.totalRepayment,
    totalSaving: currentMonthly.totalRepayment - newMonthly.totalRepayment,
    currentInterest: currentMonthly.totalInterest,
    newInterest: newMonthly.totalInterest
  };
}

export function calculateLoanRepayment({ loanAmount, annualRate, termYears, frequency = 'Monthly' }) {
  const periodsPerYear = frequency === 'Weekly' ? 52 : frequency === 'Fortnightly' ? 26 : 12;
  const totalPeriods = Math.max(1, termYears * periodsPerYear);
  const periodicRate = annualRate / 100 / periodsPerYear;

  if (periodicRate === 0) {
    const payment = loanAmount / totalPeriods;
    return {
      periodicPayment: payment,
      totalRepayment: loanAmount,
      totalInterest: 0,
      periodsPerYear,
      totalPeriods,
    };
  }

  const periodicPayment = loanAmount * ((periodicRate * Math.pow(1 + periodicRate, totalPeriods)) / (Math.pow(1 + periodicRate, totalPeriods) - 1));
  const totalRepayment = periodicPayment * totalPeriods;
  const totalInterest = totalRepayment - loanAmount;
  return {
    periodicPayment,
    totalRepayment,
    totalInterest,
    periodsPerYear,
    totalPeriods,
  };
}

export function calculateBorrowingPower({ monthlySurplus, annualRate, termYears }) {
  const months = Math.max(1, termYears * 12);
  const monthlyRate = annualRate / 100 / 12;
  if (monthlySurplus <= 0) return 0;
  if (monthlyRate === 0) return monthlySurplus * months;
  return monthlySurplus * ((1 - Math.pow(1 + monthlyRate, -months)) / monthlyRate);
}

export function calculateCashflowImpact({ monthlyRevenue, monthlyCosts, currentRepayment, newRepayment }) {
  const netBefore = monthlyRevenue - monthlyCosts - currentRepayment;
  const netAfter = monthlyRevenue - monthlyCosts - newRepayment;
  const monthlyImpact = netAfter - netBefore;
  const annualImpact = monthlyImpact * 12;
  return {
    netBefore,
    netAfter,
    monthlyImpact,
    annualImpact,
    repaymentBurdenBefore: monthlyRevenue > 0 ? (currentRepayment / monthlyRevenue) * 100 : 0,
    repaymentBurdenAfter: monthlyRevenue > 0 ? (newRepayment / monthlyRevenue) * 100 : 0,
  };
}

export function formatCurrency(num) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(num);
}
